/**
 * "The recording has ended" for someone who is not looking at the phone
 * (docs/partial/shopping-in-store-spec.md 6.1): a short tone and a vibration. Both are best
 * effort – a platform without Web Audio or without a vibration motor stays silent.
 */
type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export function signalDictationEnd(kind: 'finished' | 'discarded' = 'finished'): void {
  try {
    navigator.vibrate?.(kind === 'finished' ? 30 : [30, 60, 30]);
  } catch {
    // not allowed without a user gesture on some platforms
  }
  const Ctor = audioContextCtor();
  if (!Ctor) return;
  try {
    const context = new Ctor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    // Falling for "done", low for "thrown away".
    oscillator.frequency.setValueAtTime(kind === 'finished' ? 880 : 330, context.currentTime);
    if (kind === 'finished')
      oscillator.frequency.exponentialRampToValueAtTime(587, context.currentTime + 0.16);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.2);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.22);
    oscillator.onended = () => void context.close().catch(() => undefined);
  } catch {
    // no sound then
  }
}
